import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { blogService } from '../services/blogService';
import { BlogPost } from '../types/blog';
import { COLORS } from '../constants/colors';
import { useTranslation } from 'react-i18next';

const BlogContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const BlogTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 2rem;
  color: #1a1a1a;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const BlogCard = styled.article`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const BlogCardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const BlogCardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const BlogCardLink = styled(Link)`
  color: #1a1a1a;
  text-decoration: none;

  &:hover {
    color: ${COLORS.PRIMARY};
  }
`;

const BlogCardDescription = styled.p`
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const BlogCardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #888;
  margin-bottom: 0.5rem;
`;

const BlogCardCategoryContainer = styled.div`
  margin-top: 1rem;
`;

const BlogCardCategory = styled.span`
  display: inline-block;
  background-color: ${COLORS.BLOG_PRIMARY_BACKGROUND};
  color: ${COLORS.PRIMARY};
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`;

const ErrorMessage = styled.p`
  color: #dc2626;
  text-align: center;
  padding: 2rem;
`;

const NoPostsMessage = styled.p`
  text-align: center;
  color: #888;
  padding: 2rem;
`;

const BlogList: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  
  const { lang } = useParams<{ lang?: string }>();
  
  const language = lang || 'en';

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const data = await blogService.getAllPosts(language);
        setPosts(data);
      } catch (err) {
        setError('Failed to load blog posts');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [language]);

  if (loading) {
    return (
      <BlogContainer>
        <p>{t('blog.loadingPosts')}</p>
      </BlogContainer>
    );
  }

  if (error) {
    return (
      <BlogContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </BlogContainer>
    );
  }

  const blogBasePath = language === 'en' ? '/blog' : `/${language}/blog`;

  return (
    <BlogContainer>
      <BlogTitle>{language === 'ro' ? 'Blog' : 'Blog'}</BlogTitle>
      
      <BlogGrid>
        {posts.map((post) => (
          <BlogCard key={post._id}>
            {post.featuredImage && (
              <BlogCardImage 
                src={post.featuredImage} 
                alt={post.title}
              />
            )}
            
            <BlogCardTitle>
              <BlogCardLink to={`${blogBasePath}/${post.slug}`}>
                {post.title}
              </BlogCardLink>
            </BlogCardTitle>
            
            <BlogCardDescription>{post.metaDescription}</BlogCardDescription>
            
            <BlogCardMeta>
              <span>
                {new Date(post.publishedDate).toLocaleDateString(
                  language === 'ro' ? 'ro-RO' : 'en-US',
                  { year: 'numeric', month: 'long', day: 'numeric' }
                )}
              </span>
              {post.readTime && <span>{post.readTime} {t('blog.minRead')}</span>}
            </BlogCardMeta>
            
            {post.category && (
              <BlogCardCategoryContainer>
                <BlogCardCategory>{post.category}</BlogCardCategory>
              </BlogCardCategoryContainer>
            )}
          </BlogCard>
        ))}
      </BlogGrid>

      {posts.length === 0 && (
        <NoPostsMessage>{t('blog.noPosts')}</NoPostsMessage>
      )}
    </BlogContainer>
  );
};

export default BlogList;